/*
 * *
 *   ~  * Copyright (c) by INFODATA-GROUP. All rights reserved.
 *   ~  * No part of this publication may be reproduced, distributed,
 *   ~  * or transmitted in any form or by any means, including photocopying, recording,
 *   ~  * or other electronic or mechanical methods, without the prior written permission of the publisher,
 *   ~  * except in the case of brief quotations embodied in critical
 *   ~  * reviews and certain other noncommercial uses permitted by copyright law.
 *   ~  * AUTHOR : bs
 *   ~  * DATE : 29/04/2025 17:12
 *
 */

import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  ElementRef,
  EventEmitter,
  input,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import {
  ActionEvent,
  DialogType,
  DropdownAction,
  GroupAction,
  StandardAction,
  SwitchAction,
} from '@infodata-group/inf-commons';
import { DialogComponent } from '@infodata-group/inf-commons-desktop';
import { TranslateModule } from '@ngx-translate/core';
import { DrawingHeaderActionComponent } from './drawing-header-action/drawing-header-action.component';
import {
  COLOR_ACTION,
  COLORS,
  DELETE_ACTION,
  DRAW_ACTION,
  ERASER_ACTION,
  HIGHLIGHT_ACTION,
  IMPORT_BACKGROUND_ACTION,
  MOVE_ACTION,
  REDO_ACTION,
  UNDO_ACTION,
  ZOOM_IN_ACTION,
  ZOOM_OUT_ACTION,
} from './drawing-header.constant';
import { of } from 'rxjs';

@Component({
  selector: 'infl-drawing-header',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    DrawingHeaderActionComponent,
    DialogComponent,
  ],
  templateUrl: './drawing-header.component.html',
  styleUrl: './drawing-header.component.scss',
})
export class DrawingHeaderComponent {
  protected readonly DialogType = DialogType;

  public enableHistory = input.required<boolean>();
  public enableBackground = input.required<boolean>();
  public enableZoom = input.required<boolean>();
  public enableMove = input.required<boolean>();

  public textCancel = input<string>('Annuler');
  public textSave = input<string>('Ok');

  // #region DrawThickness
  public drawThickness = input.required<number>();
  @ViewChild('inputDraw') inputDraw: ElementRef<HTMLInputElement>;
  private getInputDrawValue(): number {
    return parseInt(this.inputDraw.nativeElement.value);
  }
  // #endregion
  // #region HighlightThickness
  public highlightThickness = input.required<number>();
  @ViewChild('inputHighlight') inputHighlight: ElementRef<HTMLInputElement>;
  private getInputHighlightValue(): number {
    return parseInt(this.inputHighlight.nativeElement.value);
  }
  // #endregion
  // #region EraserThickness
  public eraserThickness = input.required<number>();
  @ViewChild('inputEraser') inputEraser: ElementRef<HTMLInputElement>;
  private getInputEraserValue(): number {
    return parseInt(this.inputEraser.nativeElement.value);
  }

  // #endregion

  protected groupActions = computed(() => {
    return [
      this.historyActionGroup(),
      new GroupAction('draw', [
        DRAW_ACTION,
        HIGHLIGHT_ACTION,
        COLOR_ACTION,
        ERASER_ACTION,
      ]),
      this.zoomMoveActionGroup(),
      this.plusActionGroup(),
    ].filter((g) => g !== null);
  });
  protected historyActionGroup = computed(() => {
    if (!this.enableHistory()) {
      return null;
    }
    return new GroupAction('history', [UNDO_ACTION, REDO_ACTION]);
  });
  protected zoomMoveActionGroup = computed(() => {
    if (!this.enableZoom() && !this.enableMove()) {
      return null;
    }
    const group = new GroupAction('zoom-move', []);
    if (this.enableZoom()) {
      group.actions.push(ZOOM_OUT_ACTION, ZOOM_IN_ACTION);
    }
    if (this.enableMove()) {
      group.actions.push(MOVE_ACTION);
    }
    return group;
  });
  protected plusActionGroup = computed(() => {
    const plusActions = [DELETE_ACTION];
    // const plusActions = [DELETE_ACTION, FULLSCREEN_ACTION];
    if (this.enableBackground()) {
      plusActions.push(IMPORT_BACKGROUND_ACTION);
    }

    const dropdownAction = new DropdownAction(
      'plus',
      'ellipsis-v',
      of(plusActions),
    );
    return new GroupAction('plus', [dropdownAction]);
  });

  protected colors = signal<StandardAction[]>(COLORS);

  protected focusActionName = computed(() => {
    if (this.focusAction()) {
      return this.focusAction().name;
    }
    return '';
  });

  protected selectedAction = signal<StandardAction>(DRAW_ACTION);
  protected focusAction = signal<StandardAction>(null);

  private eventListener = null;

  @Output() actionClick = new EventEmitter<ActionEvent>();

  @ViewChild(DialogComponent) dialog: DialogComponent;

  protected onClickAction($event: ActionEvent): void {
    this.handleSwitchAction($event);
    switch ($event.action.name) {
      case DELETE_ACTION.name:
        this.onClickDeleteAction();
        break;
      case COLOR_ACTION.name:
        this.setFocusAction($event.action);
        break;
      case DRAW_ACTION.name:
        $event.value = this.getInputDrawValue();
        this.selectedAction.set($event.action);
        this.actionClick.emit($event);
        break;
      case HIGHLIGHT_ACTION.name:
        $event.value = this.getInputHighlightValue();
        this.selectedAction.set($event.action);
        this.actionClick.emit($event);
        break;
      case ERASER_ACTION.name:
        $event.value = this.getInputEraserValue();
        this.selectedAction.set($event.action);
        this.actionClick.emit($event);
        break;
      default:
        this.selectedAction.set($event.action);
        this.actionClick.emit($event);
        break;
    }
  }

  protected onLongPress($event: ActionEvent): void {
    this.handleSwitchAction($event);
    switch ($event.action.name) {
      case DRAW_ACTION.name:
        this.setFocusAction($event.action);
        this.selectedAction.set($event.action);
        $event.value = this.getInputDrawValue();
        this.actionClick.emit($event);
        break;
      case HIGHLIGHT_ACTION.name:
        this.setFocusAction($event.action);
        this.selectedAction.set($event.action);
        $event.value = this.getInputHighlightValue();
        this.actionClick.emit($event);
        break;
      case ERASER_ACTION.name:
        this.setFocusAction($event.action);
        this.selectedAction.set($event.action);
        $event.value = this.getInputEraserValue();
        this.actionClick.emit($event);
        break;
    }
  }

  private onClickDeleteAction(): void {
    this.dialog.open();
  }

  protected onActionDialog($event: string): void {
    if ($event === 'confirm') {
      this.actionClick.emit(new ActionEvent('click', DELETE_ACTION));
    }
  }

  protected onClickColor(color: StandardAction) {
    COLOR_ACTION.iconColor = color.text;
    this.actionClick.emit(new ActionEvent('click', COLOR_ACTION, color.text));
  }

  protected onCancelClick(): void {
    this.actionClick.emit(
      new ActionEvent('click', new StandardAction('cancel', '')),
    );
  }

  protected onSaveClick(): void {
    this.actionClick.emit(
      new ActionEvent('click', new StandardAction('save', '')),
    );
  }

  private handleSwitchAction($event: ActionEvent): void {
    if (this.selectedAction().name === $event.action.name) {
      this.setFocusAction($event.action);
      return;
    }

    if ($event.action instanceof SwitchAction) {
      this.groupActions()
        .map((g) => g.actions)
        .flat()
        .filter((a) => a instanceof SwitchAction)
        .forEach((a) => {
          a.isActive = false;
        });
      $event.action.isActive = true;
      this.selectedAction.set($event.action);
    }
  }

  protected onChangeDrawThickness(): void {
    this.actionClick.emit(
      new ActionEvent('click', DRAW_ACTION, this.getInputDrawValue()),
    );
    this.setFocusAction(DRAW_ACTION);
  }

  protected onChangeHighlightThickness(): void {
    this.actionClick.emit(
      new ActionEvent('click', HIGHLIGHT_ACTION, this.getInputHighlightValue()),
    );
    this.setFocusAction(HIGHLIGHT_ACTION);
  }

  protected onChangeEraserThickness(): void {
    this.actionClick.emit(
      new ActionEvent('click', ERASER_ACTION, this.getInputEraserValue()),
    );
    this.setFocusAction(ERASER_ACTION);
  }

  private setFocusAction(action: StandardAction): void {
    this.focusAction.set(action);
    this.eventListener = document.addEventListener(
      'click',
      () => {
        this.focusAction.set(null);
      },
      { once: true },
    );
  }

  public clearFocusAction(): void {
    this.setFocusAction(null);
    removeEventListener('click', this.eventListener);
  }
}
